import {
  Search,
  Filter,
  Plus,
  Minus,
  Mail,
  ArrowRight,
  Brain,
  Zap,
  Target,
  DollarSign,
  Users,
  MessageSquare,
} from "lucide-react";
import { Link } from "react-router-dom";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { useState } from "react";

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const categories = [
    { id: "all", name: "All Questions", icon: Filter },
    { id: "general", name: "General", icon: MessageSquare },
    { id: "features", name: "Features & Usage", icon: Zap },
    { id: "ai", name: "AI Technology", icon: Brain },
    { id: "pricing", name: "Pricing & Plans", icon: DollarSign },
    { id: "account", name: "Account & Settings", icon: Users },
  ];

  const faqs = [
    {
      id: "general-1",
      question: "What is Blyrha?",
      answer:
        "Blyrha is an AI-powered marketing automation platform designed to help businesses create more effective campaigns with less effort. Our platform uses advanced artificial intelligence to generate content, optimize targeting, analyze performance, and automate routine marketing tasks.",
      category: "general",
    },
    {
      id: "general-2",
      question: "How do I get started with Blyrha?",
      answer:
        "Getting started is easy! Sign up for a free 14-day trial (no credit card required), connect your marketing accounts, and follow our guided setup process. Our onboarding wizard will help you set up your first campaign in minutes. You can also schedule a demo with our team for personalized guidance.",
      category: "general",
    },
    {
      id: "features-1",
      question: "What marketing channels does Blyrha support?",
      answer:
        "Blyrha integrates with all major marketing channels including email, social media (Facebook, Instagram, LinkedIn, Twitter), content management systems, Google Ads, and more. We're constantly adding new integrations to provide a comprehensive marketing solution.",
      category: "features",
    },
    {
      id: "features-2",
      question: "Can I use Blyrha for email marketing automation?",
      answer:
        "Yes! Email marketing is one of our core features. Blyrha can help you create email campaigns, segment your audience, personalize content, automate sequences, A/B test variations, and analyze performance—all enhanced by AI for better results.",
      category: "features",
    },
    {
      id: "features-3",
      question: "Does Blyrha offer content creation tools?",
      answer:
        "Absolutely. Our AI content generation tools can help you create blog posts, social media updates, email copy, ad copy, and more. You can specify your brand voice, target audience, and goals, and our AI will generate tailored content that you can edit and refine.",
      category: "features",
    },
    {
      id: "ai-1",
      question: "How does Blyrha's AI technology work?",
      answer:
        "Blyrha uses advanced machine learning models and natural language processing to analyze your marketing data, understand audience preferences, and generate optimized content. Our AI continuously learns from campaign performance to improve recommendations and automation over time.",
      category: "ai",
    },
    {
      id: "ai-2",
      question: "Is the AI content unique and original?",
      answer:
        "Yes, all content generated by Blyrha's AI is unique and original. Our system creates fresh content for each request based on your inputs and guidelines. You always maintain full editorial control, and can edit, refine or regenerate any content as needed.",
      category: "ai",
    },
    {
      id: "ai-3",
      question: "Can Blyrha's AI understand my brand voice?",
      answer:
        "Yes, our AI can learn and adapt to your brand voice. During onboarding, you can provide examples of your existing content, specify tone preferences, and set brand guidelines. The more you use the platform, the better it will understand and replicate your unique brand voice.",
      category: "ai",
    },
    {
      id: "pricing-1",
      question: "How much does Blyrha cost?",
      answer:
        "Blyrha offers several pricing tiers to fit businesses of all sizes. Our Starter plan begins at $29/month, the Professional plan at $99/month, and Enterprise plans start at $299/month. We offer a 20% discount for annual billing. Visit our Pricing page for detailed information on what each plan includes.",
      category: "pricing",
    },
    {
      id: "pricing-2",
      question: "Do you offer a free trial?",
      answer:
        "Yes, we offer a 14-day free trial with full access to our Professional plan features. No credit card is required to start your trial, and you can cancel anytime. This gives you the chance to experience all the core features before making a decision.",
      category: "pricing",
    },
    {
      id: "pricing-3",
      question: "Can I change my plan later?",
      answer:
        "Absolutely! You can upgrade, downgrade, or cancel your plan at any time. If you upgrade, the changes take effect immediately. If you downgrade, the changes will take effect at the start of your next billing cycle. We provide prorated credits for plan changes when applicable.",
      category: "pricing",
    },
    {
      id: "account-1",
      question: "How many team members can I add to my account?",
      answer:
        "The number of team members depends on your plan. The Starter plan includes 1 user, the Professional plan allows up to 5 users, and Enterprise plans support unlimited users. Each user gets their own login credentials with permission levels you can customize.",
      category: "account",
    },
    {
      id: "account-2",
      question: "How secure is my data with Blyrha?",
      answer:
        "Data security is our top priority. We use industry-standard encryption for all data, both in transit and at rest. We maintain strict access controls, regular security audits, and comply with GDPR, CCPA, and other privacy regulations. Your marketing data is never shared with third parties without your explicit permission.",
      category: "account",
    },
    {
      id: "account-3",
      question: "What happens to my data if I cancel my subscription?",
      answer:
        "If you cancel your subscription, you'll have 30 days to export your data before it's removed from our active systems. After that period, data is moved to secure archive storage for a limited time as required by law, then permanently deleted. We never retain or use your data after account closure.",
      category: "account",
    },
  ];

  const handleToggleItem = (id: string) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory =
      selectedCategory === "all" || faq.category === selectedCategory;
    const matchesSearch =
      searchQuery === "" ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Wrapper className="py-20 relative" data-oid="0iqg.-8">
      {/* Hero Section */}
      <Container data-oid="mk0azdo">
        <div className="text-center max-w-4xl mx-auto mb-16" data-oid="hw_1rg1">
          <motion.h1
            className="text-4xl md:text-6xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-oid=".62j07t"
          >
            Frequently Asked
            <span
              className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
              data-oid="pr74zpn"
            >
              {" "}
              Questions
            </span>
          </motion.h1>
          <motion.p
            className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-oid="dcs23a."
          >
            Find answers to commonly asked questions about Blyrha's AI-powered
            marketing platform and services.
          </motion.p>
        </div>
      </Container>

      {/* Search and Categories */}
      <Container data-oid="z:8z243">
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          data-oid="4t76wa6"
        >
          {/* Search Bar */}
          <div className="max-w-md mx-auto mb-8" data-oid="27r60i5">
            <div className="relative" data-oid="dnoj04l">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
                data-oid="6:dxz2r"
              />
              <Input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-3"
                data-oid="yhy45mq"
              />
            </div>
          </div>

          {/* Categories */}
          <div
            className="flex flex-wrap justify-center gap-3 mb-12"
            data-oid="mnkf425"
          >
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-200 ${
                    selectedCategory === category.id
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-background hover:bg-muted border-border"
                  }`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  data-oid="ncpzw05"
                >
                  <Icon className="w-4 h-4" data-oid="oor3eyc" />
                  {category.name}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </Container>

      {/* FAQ Accordion */}
      <Container data-oid="pm:0-t.">
        <motion.div
          className="max-w-3xl mx-auto mb-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          data-oid="x5l.idp"
        >
          {filteredFaqs.length > 0 ? (
            <div className="space-y-4" data-oid="gnjy7fw">
              {filteredFaqs.map((faq, index) => {
                const isExpanded = expandedItems.includes(faq.id);
                return (
                  <motion.div
                    key={faq.id}
                    className="border rounded-2xl overflow-hidden bg-gradient-to-r from-slate-50 to-white dark:from-slate-900/60 dark:to-slate-900"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                    data-oid="6_myyhg"
                  >
                    <button
                      className={`group w-full text-left p-6 flex justify-between items-center gap-4 focus:outline-none transition-all duration-200 border-b ${
                        isExpanded
                          ? "bg-white/95 text-slate-900 dark:bg-slate-900/80 dark:text-white border-blue-100 shadow-lg"
                          : "bg-card text-slate-800 dark:bg-slate-900/60 dark:text-slate-100 hover:bg-muted/80"
                      }`}
                      onClick={() => handleToggleItem(faq.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`${faq.id}-content`}
                      data-oid="9sapgdo"
                    >
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${
                          isExpanded
                            ? "border-blue-200 bg-blue-50 text-blue-600"
                            : "border-transparent bg-muted text-muted-foreground"
                        }`}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </div>
                      <h3 className="text-lg font-semibold flex-1" data-oid="cobeq8-">
                        {faq.question}
                      </h3>
                      {isExpanded ? (
                        <Minus
                          className="w-5 h-5 flex-shrink-0 text-blue-500"
                          data-oid="79gb3sv"
                        />
                      ) : (
                        <Plus
                          className="w-5 h-5 flex-shrink-0 text-muted-foreground group-hover:text-blue-500"
                          data-oid="1:ltj0b"
                        />
                      )}
                    </button>
                    {isExpanded && (
                      <motion.div
                        id={`${faq.id}-content`}
                        className="p-6 pt-4 text-slate-600 dark:text-slate-200 bg-gradient-to-b from-blue-50/80 to-white dark:from-slate-900/60 dark:to-slate-900 border-t border-blue-100"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        transition={{ duration: 0.3 }}
                        data-oid="sf9h6wr"
                      >
                        <p data-oid="_rttd5s">{faq.answer}</p>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center p-12" data-oid="b3mg9au">
              <Search
                className="w-12 h-12 text-muted-foreground mx-auto mb-4"
                data-oid="c4b13::"
              />
              <h3 className="text-xl font-semibold mb-2" data-oid="hnbvsc4">
                No matching questions found
              </h3>
              <p className="text-muted-foreground mb-6" data-oid="r.1i:h0">
                Try adjusting your search terms or category filter.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                }}
                data-oid="1r4fia4"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </motion.div>
      </Container>

      {/* Still Have Questions Section */}
      <Container data-oid="3_we25o">
        <motion.div
          className="relative overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-slate-900 text-white mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="q:9f.g-"
        >
          <div className="absolute inset-0 opacity-80 bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600" />
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.6),_transparent_60%)]" />
          <div className="relative px-6 py-12 md:px-12 flex flex-col gap-8 text-center">
            <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 py-1 text-sm font-medium uppercase tracking-wide">
              <span className="h-2 w-2 rounded-full bg-emerald-300" />
              Real people. Real answers.
            </div>
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold leading-tight" data-oid="rb5axkb">
                Still have questions?
              </h2>
              <p
                className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto"
                data-oid="w1.vvo_"
              >
                Our success engineers respond within 12 hours on weekdays and tailor guidance for your stack. Tell us what you're building and we'll help you ship faster.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-oid="kb.:g_h">
              <Link to="/contact" data-oid="m1il7oy">
                <Button size="lg" className="px-8 gap-2 shadow-lg shadow-blue-900/30" data-oid="3y2wsbm">
                  <Mail className="h-4 w-4" data-oid="mw7-qvq" />
                  Contact Support
                </Button>
              </Link>
              <Link to="/features" data-oid="599otm4">
                <Button
                  size="lg"
                  variant="secondary"
                  className="px-8 gap-2 bg-white/10 text-white hover:bg-white/20 border border-white/30"
                  data-oid="ativyqj"
                >
                  Explore Features
                  <ArrowRight className="h-4 w-4" data-oid="a7su-42" />
                </Button>
              </Link>
            </div>
            <div className="flex flex-col gap-2 text-sm text-white/70">
              <span>Average response time: <strong className="text-white">&lt; 12 hrs</strong></span>
              <span>Prefer a call? <Link to="/contact" className="underline underline-offset-4 text-white" data-oid="call-link">Schedule a demo</Link></span>
            </div>
          </div>
        </motion.div>
      </Container>

      {/* Common Topics */}
      <Container data-oid="c9jo:04">
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="gky6nnc"
        >
          <h2
            className="text-3xl font-bold mb-8 text-center"
            data-oid="y:qza1p"
          >
            Popular Topics
          </h2>
          <div className="grid md:grid-cols-3 gap-6" data-oid="cirzc-k">
            {[
              {
                title: "Getting Started Guide",
                description:
                  "Step-by-step instructions for setting up your first AI marketing campaign",
                icon: Target,
                link: "#",
              },
              {
                title: "AI Content Generation",
                description:
                  "Learn how our AI creates compelling marketing content for any channel",
                icon: Brain,
                link: "#",
              },
              {
                title: "Platform Integrations",
                description:
                  "Connect Blyrha with your favorite marketing tools and platforms",
                icon: Zap,
                link: "#",
              },
            ].map((topic, index) => {
              const TopicIcon = topic.icon;
              return (
                <motion.div
                  key={index}
                  className="p-6 rounded-xl border bg-card hover:shadow-lg transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-oid="2ppawkn"
                >
                  <TopicIcon
                    className="w-10 h-10 text-blue-500 mb-4"
                    data-oid="j8o4ts."
                  />
                  <h3 className="text-xl font-semibold mb-2" data-oid="g7wm5pc">
                    {topic.title}
                  </h3>
                  <p className="text-muted-foreground mb-4" data-oid="ijwm0sy">
                    {topic.description}
                  </p>
                  <Link to={topic.link} data-oid="xcpj13w">
                    <Button
                      variant="outline"
                      className="w-full"
                      data-oid="rtn7o4e"
                    >
                      View Guide
                      <ArrowRight className="w-4 h-4 ml-2" data-oid=":c4-x.2" />
                    </Button>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </Container>

      {/* Newsletter */}
      <Container data-oid=".ej.f.4">
        <motion.div
          className="text-center p-12 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="pony054"
        >
          <h2 className="text-3xl font-bold mb-4" data-oid="_4xp9o6">
            Stay Updated
          </h2>
          <p
            className="text-xl mb-8 opacity-90 max-w-2xl mx-auto"
            data-oid="jn29gfu"
          >
            Subscribe to our newsletter for the latest AI marketing tips,
            features, and best practices.
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto"
            data-oid="86:nbe_"
          >
            <Input
              type="email"
              placeholder="Enter your email"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/70"
              data-oid="orhsi4o"
            />

            <Button variant="secondary" className="px-8" data-oid="ikh3_5c">
              Subscribe
            </Button>
          </div>
          <p className="text-sm opacity-70 mt-4" data-oid="cjk::tm">
            No spam. Unsubscribe anytime.
          </p>
        </motion.div>
      </Container>
    </Wrapper>
  );
};

export default FAQPage;
